import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Package, AlertCircle } from 'lucide-react';
import { ShipmentLineDto, TransitPackagingDto, ShipmentLineRow } from '../../../types/shipmentLine';
import { transitPackagingService } from '../../../services/transitPackagingService';

interface ShipmentLineEditorProps {
  pListId: number;
  orderNo: number;
  initialLines?: ShipmentLineDto[];
  onChange: (lines: ShipmentLineRow[]) => void;
  disabled?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Når true: 1 kolli per linje, antall låst til 1, "Legg til kolli"-knapp skjult */
  singleParcelMode?: boolean;
}

/**
 * Gjenbrukbar komponent for å registrere kolli-dimensjoner (ShipmentLine).
 * Brukes i DeliveryInfoModal og PackingDetailView.
 */
export const ShipmentLineEditor: React.FC<ShipmentLineEditorProps> = ({
  pListId,
  orderNo,
  initialLines = [],
  onChange,
  disabled = false,
  expanded: controlledExpanded,
  onExpandedChange,
  singleParcelMode = false
}) => {
  const [lines, setLines] = useState<ShipmentLineRow[]>([]);
  const [transitPackagings, setTransitPackagings] = useState<TransitPackagingDto[]>([]);
  const [isExpanded, setIsExpanded] = useState(controlledExpanded ?? false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync controlled expanded state
  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setIsExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  // Load transit packagings
  useEffect(() => {
    const loadTransitPackagings = async () => {
      try {
        const data = await transitPackagingService.getAll();
        setTransitPackagings(data);
      } catch (error) {
        console.error('Failed to load transit packagings:', error);
      }
    };
    loadTransitPackagings();
  }, []);

  // Initialize lines from props
  useEffect(() => {
    const mappedLines: ShipmentLineRow[] = initialLines.map(line => ({
      lineID: line.lineID,
      transitPackagingID: line.transitPackagingID,
      transitPackagingDescr: line.transitPackagingDescr,
      description1: line.description1,
      numOfPackages: singleParcelMode ? 1 : line.numOfPackages,
      volume: line.volume,
      weight: line.weight,
      height: line.height,
      width: line.width,
      length: line.length,
      pListID: line.pListID,
      orderNo: line.orderNo,
      sorting: line.sorting,
      isNew: false
    }));
    setLines(mappedLines);
  }, [initialLines, singleParcelMode]);

  // Notify parent of changes
  const notifyChange = useCallback((newLines: ShipmentLineRow[]) => {
    setLines(newLines);
    onChange(newLines);
  }, [onChange]);

  const handleExpandedToggle = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  const handleAddLine = () => {
    const newLine: ShipmentLineRow = {
      tempId: -Date.now(),
      lineID: '',
      transitPackagingID: null,
      transitPackagingDescr: null,
      description1: '',
      numOfPackages: 1,
      volume: null,
      weight: null,
      height: null,
      width: null,
      length: null,
      pListID: pListId,
      orderNo: orderNo,
      sorting: lines.length,
      isNew: true
    };
    notifyChange([...lines, newLine]);
  };

  const handleRemoveLine = (index: number) => {
    const newLines = lines.filter((_, i) => i !== index);
    notifyChange(newLines);
  };

  const handleLineChange = (index: number, field: keyof ShipmentLineRow, value: any) => {
    const newLines = [...lines];
    const line = { ...newLines[index], [field]: value, isModified: true };

    // In singleParcelMode, force numOfPackages to 1
    if (singleParcelMode && field === 'numOfPackages') {
      line.numOfPackages = 1;
    }

    // If transit packaging changed, apply defaults
    if (field === 'transitPackagingID' && value) {
      const tp = transitPackagings.find(t => t.transitPackagingID === value);
      if (tp) {
        line.transitPackagingDescr = tp.transitPackagingDescr;
        if (tp.weight) line.weight = tp.weight;
        if (tp.volume) line.volume = tp.volume;
        if (tp.height) line.height = tp.height;
        if (tp.width) line.width = tp.width;
        if (tp.length) line.length = tp.length;
      }
    }

    // Auto-calculate volume if all dimensions are set
    if (['height', 'width', 'length'].includes(field as string)) {
      const h = field === 'height' ? value : line.height;
      const w = field === 'width' ? value : line.width;
      const l = field === 'length' ? value : line.length;
      if (h && w && l) {
        line.volume = Math.round((h * w * l) / 1_000_000 * 1_000_000) / 1_000_000;
      }
    }

    newLines[index] = line;
    notifyChange(newLines);
  };

  const totalPackages = lines.reduce((sum, l) => sum + (l.numOfPackages || 0), 0);
  const totalWeight = lines.reduce((sum, l) => sum + ((l.weight || 0) * (l.numOfPackages || 0)), 0);
  const totalVolume = lines.reduce((sum, l) => sum + ((l.volume || 0) * (l.numOfPackages || 0)), 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={handleExpandedToggle}
        className="w-full flex items-center justify-between p-4 bg-purple-50 hover:bg-purple-100 transition-colors"
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <Package size={20} className="text-purple-600" />
          <span className="font-medium text-purple-900">
            Transport-detaljer
          </span>
          {lines.length > 0 && (
            <span className="text-sm text-purple-600 ml-2">
              ({totalPackages} kolli, {totalWeight.toFixed(2)} kg, {totalVolume.toFixed(4)} m³)
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-purple-600" />
        ) : (
          <ChevronDown size={20} className="text-purple-600" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 bg-white">
          {isLoading ? (
            <div className="text-center py-4 text-gray-500">Laster...</div>
          ) : (
            <>
              {/* Single parcel mode info banner */}
              {singleParcelMode && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>1 kolli per sending.</strong> Denne transportøren krever separate sendinger per kolli. Registrer vekt for hvert kolli.
                  </div>
                </div>
              )}

              {/* Table */}
              {lines.length > 0 ? (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        {!singleParcelMode && (
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Antall</th>
                        )}
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vekt (kg)</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">L (cm)</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">B (cm)</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">H (cm)</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vol (m³)</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Beskr.</th>
                        {!singleParcelMode && (
                          <th className="px-2 py-2 w-10"></th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {lines.map((line, index) => (
                        <tr key={line.lineID || line.tempId} className="hover:bg-gray-50">
                          <td className="px-2 py-2">
                            <select
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              value={line.transitPackagingID || ''}
                              onChange={(e) => handleLineChange(index, 'transitPackagingID', e.target.value ? parseInt(e.target.value) : null)}
                              disabled={disabled}
                            >
                              <option value="">-- Velg --</option>
                              {transitPackagings.map(tp => (
                                <option key={tp.transitPackagingID} value={tp.transitPackagingID}>
                                  {tp.transitPackagingDescr}
                                </option>
                              ))}
                            </select>
                          </td>
                          {!singleParcelMode && (
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                min="1"
                                className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                value={line.numOfPackages}
                                onChange={(e) => handleLineChange(index, 'numOfPackages', parseInt(e.target.value) || 1)}
                                disabled={disabled}
                              />
                            </td>
                          )}
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.01"
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={line.weight ?? ''}
                              onChange={(e) => handleLineChange(index, 'weight', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={disabled}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.1"
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={line.length ?? ''}
                              onChange={(e) => handleLineChange(index, 'length', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={disabled}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.1"
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={line.width ?? ''}
                              onChange={(e) => handleLineChange(index, 'width', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={disabled}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.1"
                              className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={line.height ?? ''}
                              onChange={(e) => handleLineChange(index, 'height', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={disabled}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              step="0.000001"
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50 focus:ring-2 focus:ring-purple-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              value={line.volume?.toFixed(6) ?? ''}
                              onChange={(e) => handleLineChange(index, 'volume', e.target.value ? parseFloat(e.target.value) : null)}
                              disabled={disabled}
                              title="Beregnes automatisk fra L×B×H"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="text"
                              maxLength={200}
                              className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              value={line.description1 || ''}
                              onChange={(e) => handleLineChange(index, 'description1', e.target.value)}
                              disabled={disabled}
                              placeholder="Beskrivelse"
                            />
                          </td>
                          {!singleParcelMode && (
                            <td className="px-2 py-2">
                              <button
                                onClick={() => handleRemoveLine(index)}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                disabled={disabled}
                                title="Slett linje"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  {singleParcelMode
                    ? 'Angi antall kolli ovenfor for å registrere vekt per kolli.'
                    : 'Ingen kolli-detaljer registrert. Klikk "Legg til kolli" for å registrere.'}
                </div>
              )}

              {/* Add button - hidden in singleParcelMode (lines are managed by DeliveryInfoModal) */}
              {!singleParcelMode && (
                <button
                  onClick={handleAddLine}
                  className="flex items-center gap-2 px-4 py-2 text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors"
                  disabled={disabled}
                >
                  <Plus size={16} />
                  Legg til kolli
                </button>
              )}

              {/* Summary */}
              {lines.length > 0 && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                  <div className="text-sm text-purple-800">
                    <strong>Totalt:</strong> {totalPackages} kolli, {totalWeight.toFixed(2)} kg, {totalVolume.toFixed(4)} m³
                    {singleParcelMode && (
                      <span className="ml-2 text-amber-700">({totalPackages} sendinger)</span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ShipmentLineEditor;
